// Editable, row-virtualized takeoff grid.
//  - frozen first 3 columns (#, Area/Room, Surface) via position: sticky
//  - keyboard: arrows / Tab / Home / End / PgUp / PgDn move, Enter / F2 / typing edits,
//    Space toggles checkboxes, Esc cancels an edit (or closes the inspector)
//  - only the rows in view (+ overscan) are rendered
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TakeoffCell from "./TakeoffCell";
import { totalValue } from "./takeoff-columns";

const ROW_H = 34;
const HEAD_H = 34;
const OVERSCAN = 8;

const isEditable = (col) => ["text", "number", "select"].includes(col.type);

export default function TakeoffGrid({
  rows, columns, resultsById, defaultsById, options, totals, locked,
  selectedLineId, onActiveLineChange, onRowClick, onEscape, actions,
}) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);
  const [active, setActive] = useState({ r: 0, c: 1 });
  const [editing, setEditing] = useState(null); // { r, c, seed }

  // Frozen column offsets.
  const lefts = useMemo(() => {
    let acc = 0;
    return columns.map((col) => {
      if (col.group !== "frozen") return null;
      const l = acc;
      acc += col.width;
      return l;
    });
  }, [columns]);
  const frozenWidth = useMemo(() => columns.filter((c) => c.group === "frozen").reduce((a, c) => a + c.width, 0), [columns]);
  const tableWidth = useMemo(() => columns.reduce((a, c) => a + c.width, 0), [columns]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight));
    ro.observe(el);
    setViewportH(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Keep the active cell inside the data after filtering / deleting.
  const maxR = rows.length - 1;
  const maxC = columns.length - 1;
  const r = Math.max(0, Math.min(active.r, maxR));
  const c = Math.max(0, Math.min(active.c, maxC));

  const focusGrid = useCallback(() => scrollRef.current?.focus({ preventScroll: true }), []);

  const moveTo = useCallback(
    (nr, nc) => {
      const rr = Math.max(0, Math.min(nr, maxR));
      const cc = Math.max(0, Math.min(nc, maxC));
      setActive({ r: rr, c: cc });
      if (rows[rr]) onActiveLineChange(rows[rr].id);
    },
    [maxR, maxC, rows, onActiveLineChange]
  );

  const move = useCallback(
    (dir) => {
      if (dir === "down") moveTo(r + 1, c);
      else if (dir === "up") moveTo(r - 1, c);
      else if (dir === "right") c < maxC ? moveTo(r, c + 1) : moveTo(r + 1, 1);
      else if (dir === "left") c > 1 ? moveTo(r, c - 1) : moveTo(r - 1, maxC);
    },
    [moveTo, r, c, maxC]
  );

  // Scroll the active cell into view (rows: virtual math, columns: measured).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = r * ROW_H;
    const visibleH = el.clientHeight - HEAD_H - ROW_H; // minus sticky header + footer
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ROW_H > el.scrollTop + visibleH) el.scrollTop = top + ROW_H - visibleH;
    const cell = document.getElementById(`tk-${r}-${c}`);
    if (cell && columns[c]?.group !== "frozen") {
      const left = cell.offsetLeft;
      const right = left + cell.offsetWidth;
      if (left < el.scrollLeft + frozenWidth) el.scrollLeft = left - frozenWidth;
      else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth;
    }
  }, [r, c, columns, frozenWidth]);

  // Stable callbacks for memoised cells — always call the latest closures.
  const latest = useRef({});
  latest.current = {
    commit: (lineId, col, value, dir) => {
      if (col.field === "surfaceId") actions.changeSurface(lineId, value);
      else actions.updateLine(lineId, { [col.field]: value });
      setEditing(null);
      if (dir) move(dir);
      focusGrid();
    },
    cancel: () => {
      setEditing(null);
      focusGrid();
    },
    toggle: (lineId, col) => {
      const line = rows.find((l) => l.id === lineId);
      if (line && !locked) actions.updateLine(lineId, { [col.field]: !line[col.field] });
    },
    reset: (lineId, col) => actions.resetLineField(lineId, col.field),
    cellMouseDown: (rr, cc, e) => {
      if (editing && editing.r === rr && editing.c === cc) return;
      setActive({ r: rr, c: cc });
      onRowClick(rows[rr].id);
      if (e.detail === 2 && !locked && isEditable(columns[cc])) {
        e.preventDefault();
        setEditing({ r: rr, c: cc, seed: null });
      }
    },
  };
  const api = useMemo(
    () => ({
      commit: (...a) => latest.current.commit(...a),
      cancel: () => latest.current.cancel(),
      toggle: (...a) => latest.current.toggle(...a),
      reset: (...a) => latest.current.reset(...a),
      cellMouseDown: (...a) => latest.current.cellMouseDown(...a),
    }),
    []
  );

  const onKeyDown = (e) => {
    if (editing || !rows.length) return;
    const col = columns[c];
    const line = rows[r];
    const keys = {
      ArrowDown: () => moveTo(r + 1, c),
      ArrowUp: () => moveTo(r - 1, c),
      ArrowRight: () => moveTo(r, c + 1),
      ArrowLeft: () => moveTo(r, c - 1),
      Tab: () => move(e.shiftKey ? "left" : "right"),
      Home: () => (e.ctrlKey || e.metaKey ? moveTo(0, c) : moveTo(r, 1)),
      End: () => (e.ctrlKey || e.metaKey ? moveTo(maxR, c) : moveTo(r, maxC)),
      PageDown: () => moveTo(r + 10, c),
      PageUp: () => moveTo(r - 10, c),
      Escape: () => onEscape(),
    };
    if (keys[e.key]) {
      e.preventDefault();
      keys[e.key]();
      return;
    }
    if (locked) return;
    if ((e.key === "Enter" || e.key === "F2" || e.key === " ") && col.type === "bool") {
      e.preventDefault();
      api.toggle(line.id, col);
      return;
    }
    if ((e.key === "Enter" || e.key === "F2") && isEditable(col)) {
      e.preventDefault();
      setEditing({ r, c, seed: null });
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && isEditable(col)) {
      e.preventDefault();
      setEditing({ r, c, seed: col.type === "select" ? null : e.key });
    }
  };

  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewportH) / ROW_H) + OVERSCAN);
  const visible = rows.slice(start, end);

  return (
    <div
      ref={scrollRef}
      role="grid"
      aria-label="Takeoff lines"
      aria-rowcount={rows.length + 2}
      aria-colcount={columns.length}
      aria-activedescendant={rows.length ? `tk-${r}-${c}` : undefined}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      className="relative h-full min-h-[240px] overflow-auto rounded-[4px] border border-border bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong"
    >
      <table className="table-fixed border-separate border-spacing-0" style={{ width: tableWidth }}>
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={{ width: col.width }} />
          ))}
        </colgroup>
        <thead>
          <tr role="row" aria-rowindex={1}>
            {columns.map((col, i) => (
              <th
                key={col.key}
                role="columnheader"
                scope="col"
                style={{ height: HEAD_H, ...(lefts[i] != null ? { left: lefts[i] } : {}) }}
                className={`sticky top-0 border-b border-r border-border bg-surface_alt px-2 text-[10.5px] font-semibold uppercase tracking-wide text-text_muted whitespace-nowrap ${
                  lefts[i] != null ? "z-30" : "z-20"
                } ${col.align === "right" ? "text-right" : "text-left"} ${i === lefts.filter((l) => l != null).length - 1 ? "shadow-[2px_0_0_var(--color-border)]" : ""}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {start > 0 && <tr aria-hidden="true" style={{ height: start * ROW_H }} />}
          {visible.map((line, vi) => {
            const ri = start + vi;
            const result = resultsById[line.id];
            return (
              <tr key={line.id} role="row" aria-rowindex={ri + 2} style={{ height: ROW_H }}>
                {columns.map((col, ci) => (
                  <TakeoffCell
                    key={col.key}
                    col={col}
                    colIdx={ci}
                    rowIdx={ri}
                    line={line}
                    result={result}
                    defaults={defaultsById[line.id]}
                    options={options[col.field]}
                    editing={!!editing && editing.r === ri && editing.c === ci}
                    seed={editing && editing.r === ri && editing.c === ci ? editing.seed : null}
                    active={ri === r && ci === c}
                    selectedRow={line.id === selectedLineId}
                    locked={locked}
                    left={lefts[ci]}
                    api={api}
                  />
                ))}
              </tr>
            );
          })}
          {end < rows.length && <tr aria-hidden="true" style={{ height: (rows.length - end) * ROW_H }} />}
        </tbody>
        <tfoot>
          <tr role="row" aria-rowindex={rows.length + 2}>
            {columns.map((col, i) => {
              const v = col.type === "calc" && totals ? totalValue(col, totals) : null;
              return (
                <td
                  key={col.key}
                  style={{ height: ROW_H, ...(lefts[i] != null ? { left: lefts[i] } : {}) }}
                  className={`sticky bottom-0 border-r border-t border-border_strong bg-surface_alt px-2 text-[12.5px] font-semibold text-text_primary whitespace-nowrap ${
                    lefts[i] != null ? "z-30" : "z-20"
                  } ${col.align === "right" ? "text-right tabular-nums" : ""}`}
                >
                  {col.key === "area" ? "Totals – all lines" : v != null ? col.format(v) : ""}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
